import type { Entry } from "@/shared/types/entries";
import type { TimeEntryFormValues } from "@/v2/components/entry-form/schema";
import type { AddPayload } from "@/v2/hooks/actions/add";
import type { EditPayload } from "@/v2/hooks/actions/edit";
import { parseEntryDate } from "@/v2/lib/dates";
import { secondsToBillingUnits } from "@/v2/lib/time";

export interface AddEntryPayloadOptions {
	shouldExport?: boolean;
	timekeeperId?: string | null;
}

export interface EditEntryPayloadOptions {
	timekeeperId?: string | null;
	entryGroupId?: string | null;
	taskCodeId?: string | null;
	activityCodeId?: string | null;
}

export interface TimerEntryDefaultOptions {
	date: Date;
	timerDurationSeconds: number | null;
	carryForward: Partial<TimeEntryFormValues> | null;
}

export function createAddEntryPayload(
	values: TimeEntryFormValues,
	date: string,
	options?: AddEntryPayloadOptions,
): AddPayload {
	const payload: AddPayload = {
		id: 0,
		date,
		matter: values.matter,
		matter_id: values.matter_id,
		description: values.description,
		duration: values.duration,
		task: values.task,
		activity: values.activity ?? "",
		timekeeperId: options?.timekeeperId ?? null,
	};

	if (options?.shouldExport) payload.shouldExport = true;
	return payload;
}

export function createEditEntryPayload(
	entryId: number,
	values: TimeEntryFormValues,
	date: string,
	options?: EditEntryPayloadOptions,
): EditPayload {
	return {
		id: entryId,
		date,
		matter: values.matter,
		matter_id: values.matter_id,
		description: values.description,
		duration: values.duration,
		task: values.task,
		activity: values.activity ?? "",
		taskCodeId: options?.taskCodeId,
		activityCodeId: options?.activityCodeId,
		timekeeperId: options?.timekeeperId ?? null,
		entryGroupId: options?.entryGroupId ?? null,
	};
}

export function createEntryFormDefaults(
	entry: Entry,
): Partial<TimeEntryFormValues> {
	return {
		date: parseEntryDate(entry.date),
		matter_id: entry.matter_id ?? "",
		matter: entry.matter ?? "",
		task: entry.task ?? "",
		activity: entry.activity ?? "",
		duration: entry.duration ?? 0,
		description: entry.description ?? "",
	};
}

export function createCarryForwardValues(
	values: TimeEntryFormValues,
): Partial<TimeEntryFormValues> {
	return {
		date: values.date,
		matter_id: values.matter_id,
		matter: values.matter,
		task: values.task,
		activity: values.activity,
		duration: values.duration,
		description: values.description,
	};
}

export function createTimerEntryDefaults({
	date,
	timerDurationSeconds,
	carryForward,
}: TimerEntryDefaultOptions): Partial<TimeEntryFormValues> {
	const defaults: Partial<TimeEntryFormValues> = { date };

	if (timerDurationSeconds !== null) {
		defaults.duration = secondsToBillingUnits(timerDurationSeconds);
	}

	return carryForward ? { ...defaults, ...carryForward } : defaults;
}
